import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NextParams } from "shared/types/wizard";

import { TWizard, WizardData } from "shared/components/WizardNew/types";

import { COOKIE_WIZZARD_DATA } from "shared/constants/cookie";
import { SPLIT_TEST } from "shared/constants/split";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { simplyClick } from "shared/helpers/analytics";
import { getCookie } from "shared/helpers/cookie";
import { isSplitTestRequired } from "shared/helpers/view";
import { postWizardData } from "shared/helpers/wizard";

export enum WizardStep {
  WelcomeStep1 = "welcome",
  JoinReason = "join_reasons",
  DateBodyType = "d_body_type",
  DateAge = "age",

  WelcomeStep2 = "welcome_about_yourself",
  Bio = "bio",
  BodyType = "body_type",
  Location = "location",
  MaritalStatus = "marital_status",
  Occupation = "occupation",
  Interests = "interests",

  WelcomeStep3 = "welcome_add_photo",
  AddPhoto = "verified_user",

  FinishStep = "finish",

  OneMoreThing = "one_more_thing",
  Profiles = "profiles",
}

type WizardPredefinedAnswers = {
  join_reasons?: string[];
  body_type?: string[];
  age?: string[];
  body_type_yourself?: string[];
  marital_status?: string[];
  occupation?: string[];
  interests?: string[];
};

const getWizardCookieData = (): WizardPredefinedAnswers => {
  const cookieDataRaw = getCookie(COOKIE_WIZZARD_DATA);

  try {
    return cookieDataRaw
      ? JSON.parse(decodeURIComponent(cookieDataRaw as string))
      : {};
  } catch {
    return {};
  }
};

const firstBlockSteps: (keyof WizardPredefinedAnswers)[] = [
  "join_reasons",
  "age",
  "body_type",
];

const stepsToCheck = new Map<WizardStep, keyof WizardPredefinedAnswers>([
  [WizardStep.JoinReason, "join_reasons"],
  [WizardStep.DateAge, "age"],
  [WizardStep.DateBodyType, "body_type"],
  [WizardStep.BodyType, "body_type_yourself"],
  [WizardStep.MaritalStatus, "marital_status"],
  [WizardStep.Interests, "interests"],
  [WizardStep.Occupation, "occupation"],
]);

const prepareValueFromCookie = (step: WizardStep, value: string[]) => {
  const numericValue = value?.map(Number);

  switch (step) {
    case "age":
      return { age: { age_from: numericValue[0], age_to: numericValue[1] } };
    case "occupation":
      return { [step]: value[0] };
    default:
      return { [step]: value };
  }
};

const skippedSteps = new Set<WizardStep>();
const triggeredEvents = new Set<string>();
const postedRequests = new Set<string>();

export const useWizard = (data: any) => {
  const { t } = useTranslation("wizardNew");

  const wizardData = data;

  const isSplitReducedWizardEnabled =
    data.mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(data, SPLIT_TEST.reducedWizard);

  const isLimitedWizardFlow =
    data.mode === SAFE_MODE_WIZARD || isSplitReducedWizardEnabled;
  const cookieData = useMemo(
    () => getWizardCookieData() as Partial<WizardPredefinedAnswers> | undefined,
    []
  );
  const wizard = useMemo(() => {
    let baseOrder: WizardStep[];

    if (data.mode === SAFE_MODE_WIZARD) {
      baseOrder = [
        WizardStep.WelcomeStep1,
        WizardStep.JoinReason,
        WizardStep.DateAge,
        WizardStep.Location,
        WizardStep.Bio,
        WizardStep.AddPhoto,
        WizardStep.FinishStep,
      ];
    } else if (isSplitReducedWizardEnabled) {
      baseOrder = [
        WizardStep.WelcomeStep1,
        WizardStep.DateAge,
        WizardStep.Interests,
        WizardStep.FinishStep,
      ];
    } else {
      baseOrder = [
        WizardStep.WelcomeStep1,
        WizardStep.JoinReason,
        WizardStep.DateAge,
        WizardStep.DateBodyType,
        WizardStep.WelcomeStep2,
        WizardStep.Location,
        WizardStep.BodyType,
        WizardStep.MaritalStatus,
        WizardStep.Occupation,
        WizardStep.Interests,
        WizardStep.Bio,
        WizardStep.WelcomeStep3,
        WizardStep.AddPhoto,
        WizardStep.FinishStep,
      ];
    }
    const originalWizard: TWizard = {
      data: wizardData as WizardData,

      [WizardStep.WelcomeStep1]: {
        disableWrapper: true,
        analytics: {
          start: [window.simplyEvent.cl_wiz_welcome_start],
        },
        nextStep: isSplitReducedWizardEnabled
          ? WizardStep.DateAge
          : WizardStep.JoinReason,
      },

      [WizardStep.JoinReason]: {
        answers: wizardData?.criteria?.join_reasons,
        nextStep: WizardStep.DateAge,
        analytics: {
          choose: [window.simplyEvent.cl_wiz_rel_choose_type],
          next: [window.simplyEvent.cl_wiz_rel_next],
          skip: [window.simplyEvent.cl_wiz_rel_skip],
          back: [window.simplyEvent.cl_wiz_rel_back],
        },
        skippable: true,
        title: t("joinReason.title"),
        previousStep: undefined,
      },

      [WizardStep.DateAge]: {
        criteria: {
          age_from: { label: "From", answers: wizardData?.criteria?.age },
          age_to: {
            label: "To",
            answers: wizardData?.criteria?.age,
            reverse: true,
          },
        },
        analytics: {
          back: [window.simplyEvent.cl_wiz_mo_age_back],
          choose: [window.simplyEvent.cl_wiz_mo_age_choose],
          next: [window.simplyEvent.cl_wiz_mo_age_next],
          notProvide: [window.simplyEvent.cl_wiz_mo_age_n_matter],
          skip: [window.simplyEvent.cl_wiz_mo_age_skip],
        },
        nextStep:
          wizardData.mode === SAFE_MODE_WIZARD
            ? WizardStep.Location
            : isSplitReducedWizardEnabled
              ? WizardStep.Interests
              : WizardStep.DateBodyType,
        notProvide: { text: t("age.notProvide") },
        previousStep: WizardStep.JoinReason,
        skippable: true,
        title:
          wizardData.mode === SAFE_MODE_WIZARD
            ? t("age.multiTitle")
            : t("age.title"),
      },

      [WizardStep.DateBodyType]: {
        answers: wizardData?.criteria?.body_type,
        nextStep: WizardStep.WelcomeStep2,
        previousStep: WizardStep.DateAge,
        skippable: true,
        analytics: {
          back: [window.simplyEvent.cl_wiz_mo_body_t_back],
          choose: [window.simplyEvent.cl_wiz_mo_body_t_choose],
          next: [window.simplyEvent.cl_wiz_mo_body_t_next],
          skip: [window.simplyEvent.cl_wiz_mo_body_t_skip],
        },
        title: t("dateBodyType.title"),
      },

      [WizardStep.WelcomeStep2]: {
        disableWrapper: true,
        nextStep: WizardStep.Location,
        analytics: {
          start: [window.simplyEvent.cl_wiz_about_yourself_start],
          back: [window.simplyEvent.cl_wiz_about_yourself_back],
        },
        previousStep: WizardStep.DateBodyType,
      },

      [WizardStep.Location]: {
        criteria: {
          residence_country: { label: "Country", name: "residence_country" },
          residence_city: { label: "City", name: "residence_city" },
        },
        skippable: true,
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_life_place_back],
          chooseCountry: [window.simplyEvent.cl_wiz_us_life_country_choose],
          chooseCity: [window.simplyEvent.cl_wiz_us_life_city_choose],
          next: [window.simplyEvent.cl_wiz_us_life_place_next],
          skip: [window.simplyEvent.cl_wiz_us_life_place_skip],
        },
        title: t("location.title"),
        nextStep:
          wizardData.mode === SAFE_MODE_WIZARD
            ? WizardStep.Bio
            : WizardStep.BodyType,
        previousStep:
          wizardData.mode === SAFE_MODE_WIZARD
            ? WizardStep.DateAge
            : WizardStep.WelcomeStep2,
      },
      [WizardStep.BodyType]: {
        answers: wizardData?.criteria?.body_type_yourself,
        previousStep: WizardStep.Location,
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_body_t_back],
          choose: [window.simplyEvent.cl_wiz_us_body_t_choose],
          next: [window.simplyEvent.cl_wiz_us_body_t_next],
          skip: [window.simplyEvent.cl_wiz_us_body_t_skip],
        },
        skippable: true,
        title: t("bodyType.title"),
        nextStep: WizardStep.MaritalStatus,
      },
      [WizardStep.MaritalStatus]: {
        answers: wizardData?.criteria?.marital_status,
        previousStep: WizardStep.BodyType,
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_marital_st_back],
          choose: [window.simplyEvent.cl_wiz_us_marital_st_choose],
          next: [window.simplyEvent.cl_wiz_us_marital_st_next],
          skip: [window.simplyEvent.cl_wiz_us_marital_st_skip],
        },
        skippable: true,
        title: t("maritalStatus.title"),
        nextStep: WizardStep.Occupation,
      },
      [WizardStep.Occupation]: {
        fieldArea: {
          placeholder: "Occupation",
          name: "occupation",
          limit: 1000,
        },
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_occupation_back],
          choose: [window.simplyEvent.cl_wiz_us_occupation_choose],
          next: [window.simplyEvent.cl_wiz_us_occupation_next],
          skip: [window.simplyEvent.cl_wiz_us_occupation_skip],
        },
        previousStep: WizardStep.MaritalStatus,
        skippable: true,
        title: t("occupation.title"),
        nextStep: WizardStep.Interests,
      },
      [WizardStep.Interests]: {
        answers: wizardData?.criteria?.interests,
        previousStep: isSplitReducedWizardEnabled
          ? WizardStep.DateAge
          : WizardStep.Occupation,
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_interests_back],
          next: [window.simplyEvent.cl_wiz_us_interests_next],
          skip: [window.simplyEvent.cl_wiz_us_interests_skip],
        },
        multiple: true,
        skippable: true,
        title: t("interests.title"),
        subtitle: t("interests.subtitle"),
        nextStep: isSplitReducedWizardEnabled
          ? WizardStep.FinishStep
          : WizardStep.Bio,
      },
      [WizardStep.Bio]: {
        fieldArea: {
          placeholder: isLimitedWizardFlow
            ? "Share a bit about yourself"
            : t("bio.placeholder"),
          name: "bio",
          limit: 1000,
        },
        analytics: {
          back: [window.simplyEvent.cl_wiz_us_describe_back],
          choose: [window.simplyEvent.cl_wiz_us_describe],
          next: [window.simplyEvent.cl_wiz_us_describe_next],
          skip: [window.simplyEvent.cl_wiz_us_describe_skip],
        },
        previousStep:
          wizardData.mode === SAFE_MODE_WIZARD
            ? WizardStep.Location
            : WizardStep.Interests,
        skippable: true,
        title: t("bio.title"),
        nextStep: isLimitedWizardFlow
          ? WizardStep.AddPhoto
          : WizardStep.WelcomeStep3,
      },
      [WizardStep.WelcomeStep3]: {
        disableWrapper: true,
        nextStep: WizardStep.AddPhoto,
        analytics: {
          start: [window.simplyEvent.cl_wiz_upload_photo_start],
          back: [window.simplyEvent.cl_wiz_upload_photo_start_back],
        },
        previousStep: WizardStep.Bio,
      },
      [WizardStep.AddPhoto]: {
        skippable: true,
        nextStep: WizardStep.FinishStep,
        analytics: {
          skip: [window.simplyEvent.cl_wiz_upload_photo_skip],
          take: [window.simplyEvent.cl_wiz_upload_photo_take],
          next: [window.simplyEvent.cl_wiz_upload_photo_next],
          back: [window.simplyEvent.cl_wiz_upload_photo_back],
          add: [window.simplyEvent.cl_wiz_upload_photo_add],

          modalAdd: [window.simplyEvent.cl_wiz_upload_photo_add_2],
          modalLater: [window.simplyEvent.cl_wiz_upload_photo_later],
          modalClose: [window.simplyEvent.cl_wiz_upload_photo_x],

          loadingBack: [window.simplyEvent.cl_wiz_load_photo_back],
          loadingErrorContinue: [
            window.simplyEvent.cl_wiz_load_photo_error_continue,
          ],
          loadingErrorClose: [window.simplyEvent.cl_wiz_load_photo_error_x],

          cropRotate: [window.simplyEvent.cl_wiz_load_photo_rotate],
          cropNext: [window.simplyEvent.cl_wiz_load_photo_next],
          cropClose: [window.simplyEvent.cl_wiz_load_photo_x],

          addedDelete: [window.simplyEvent.cl_wiz_with_photo_delete],
          addedNext: [window.simplyEvent.cl_wiz_with_photo_next],
          addedBack: [window.simplyEvent.cl_wiz_with_photo_back],
          addedSkip: [window.simplyEvent.cl_wiz_with_photo_skip],
        },
        previousStep: isLimitedWizardFlow
          ? WizardStep.Bio
          : WizardStep.WelcomeStep3,
      },
      [WizardStep.FinishStep]: {
        disableWrapper: true,
        previousStep: WizardStep.AddPhoto,
        analytics: {
          start: [window.simplyEvent.cl_wiz_end_start],
          back: [window.simplyEvent.cl_wiz_end_back],
        },
      },
      [WizardStep.OneMoreThing]: {
        disableWrapper: true,
        previousStep: WizardStep.AddPhoto,
        nextStep: WizardStep.Profiles,
        analytics: { start: [window.simplyEvent.cl_wiz_omt] },
      },
      [WizardStep.Profiles]: {
        title: t("profiles.title"),
        disableWrapper: true,
        previousStep: WizardStep.OneMoreThing,
        analytics: {
          skip: [window.simplyEvent.cl_wiz_like_skip],
          like: [window.simplyEvent.cl_wiz_like],
          next: [window.simplyEvent.cl_wiz_like_start],
          back: [window.simplyEvent.cl_wiz_like_back],
        },
      },
    };

    const visibleSteps: WizardStep[] = [];

    const allFirstBlockFilled = firstBlockSteps.every((cookieKey) => {
      const value = cookieData?.[cookieKey];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });

    for (const step of baseOrder) {
      const cookieKey = stepsToCheck.get(step);

      if (cookieKey) {
        const value = cookieData?.[cookieKey];
        const isFilled = Array.isArray(value)
          ? value.length > 0
          : Boolean(value);

        if (isFilled) {
          const stepObj = originalWizard[step];
          const isSkippedValue = Array.isArray(value)
            ? value?.[0] == "0"
            : value == "0";

          if (isSkippedValue) {
            stepObj?.analytics?.skip?.forEach((ev) => {
              if (!triggeredEvents.has(ev)) {
                simplyClick(ev, { lp_autocomplete: true });
                triggeredEvents.add(ev);
              }
            });
            continue;
          }

          stepObj?.analytics?.choose?.forEach((ev) => {
            if (!triggeredEvents.has(ev)) {
              simplyClick(ev, { lp_autocomplete: true });
              triggeredEvents.add(ev);
            }
          });
          let nextParams: NextParams;
          if (cookieKey === WizardStep.DateAge) {
            const [from = 0, to = 0] = (value ?? []).map(Number);

            nextParams = {
              age_from: from,
              age_to: to,
              lp_autocomplete: true,
            };
          } else if (cookieKey === WizardStep.Occupation) {
            const occupation =
              typeof value === "string"
                ? value
                : Array.isArray(value)
                  ? String(value[0] ?? "")
                  : "";

            nextParams = {
              occupation,
              lp_autocomplete: true,
            };
          } else {
            const paramKey =
              cookieKey === WizardStep.BodyType ? "body_types" : cookieKey;
            nextParams = {
              [paramKey]: JSON.stringify((value ?? []).map(Number)),
              lp_autocomplete: true,
            };
          }

          stepObj?.analytics?.next?.forEach((ev) => {
            if (!triggeredEvents.has(ev)) {
              simplyClick(ev, nextParams);

              triggeredEvents.add(ev);
            }
          });

          if (!postedRequests.has(step)) {
            const preparedValue = prepareValueFromCookie(
              step,
              value as string[]
            );

            postWizardData(preparedValue, step);

            postedRequests.add(step);
          }

          skippedSteps.add(step);
          continue;
        }
      }

      visibleSteps.push(step);
    }

    const result: TWizard = {} as any;
    for (let i = 0; i < visibleSteps.length; i++) {
      const step = visibleSteps[i];

      // TODO: i > 1 is temporary. Change after refactoring the previousStep & nextStep logic.
      const prev = i > 1 ? visibleSteps[i - 1] : undefined;

      const next =
        i < visibleSteps.length - 1 ? visibleSteps[i + 1] : undefined;
      const cfg = originalWizard[step] ?? {};

      result[step] = {
        ...cfg,
        previousStep: prev,
        nextStep: next,
      };
    }

    if (isSplitReducedWizardEnabled) {
      result[WizardStep.WelcomeStep1] = {
        ...result[WizardStep.WelcomeStep1],
        shouldBeSkipped: true,
      };
    }

    if (allFirstBlockFilled && result[WizardStep.WelcomeStep1]) {
      result[WizardStep.WelcomeStep1] = {
        ...result[WizardStep.WelcomeStep1],
        shouldBeSkipped: data.mode !== SAFE_MODE_WIZARD,
      };

      if (!postedRequests.has(WizardStep.WelcomeStep1)) {
        postedRequests.add(WizardStep.WelcomeStep1);
        postWizardData({}, WizardStep.WelcomeStep1);
      }
    }

    result.data = wizardData as WizardData;
    return result;
  }, [cookieData, wizardData, t]);

  return wizard;
};
